(ns common.widgets.video-timeline
  (:require ["@mui/material/Slider" :refer [SliderRail SliderTrack]]
            ["@mui/material/Slider$default" :as Slider]
            ["@mui/material/Stack$default" :as Stack]
            ["react-use/lib/useMeasure$default" :as useMeasure]
            [common.util :refer [spread-obj spread-props]]
            [uix.core :refer [$ defui]]))

;; Converted to CLJS from https://github.com/mui/material-ui/blob/87a87824c8bd61bd62a4579bf412c7d682ae9607/packages/mui-base/src/useSlider/useSlider.ts#L76-L78
(defn- valueToPercent [value min max]
  (/ (* (- value min) 100)
     (- max min)))

(defn seconds-to-str [s]
  (when s
    (let [mm (str (Math/floor (/ s 60)))
          ss (str (Math/round (mod s 60)))]
      (str mm ":" (.padStart ss 2 "0")))))

(defn ->tick-intervals [video-duration max-ticks]
  (let [intervals [1 2 3 5 10 15 20 30 60 120 300 600]
        raw-tick-step (/ video-duration max-ticks)]
    (reduce (fn [acc curr]
              (if (<= raw-tick-step curr video-duration)
                (if (zero? (rem video-duration curr))
                  (assoc acc :exact-interval curr :interval curr)
                  (assoc acc :interval curr))
                acc))
            {:exact-interval nil
             :interval nil}
            (reverse intervals))))

(defn generate-timeline-ticks [video-duration max-ticks]
  (let [intervals-map (->tick-intervals video-duration max-ticks)
        tick-step (or (:exact-interval intervals-map)
                      (:interval intervals-map))]
    (vec (take-while (partial > video-duration) (range 0 video-duration tick-step)))))

(defui timeline [{:keys [timeline-selection min max] :as props}]
  (let [[measure-ref measure] (useMeasure)
        MIN_TICK_SPACING 28
        max-ticks (some-> (.-width measure) (/ MIN_TICK_SPACING) js/Math.floor)]
    ;; Need to add a container here with padding to offset
    ;; the markers that are placed outside the slider viewbox.
    ($ Stack
       {:ref measure-ref
        :sx #js {:flex 1 :pb 2}}
       (when (pos? max-ticks)
         ($ Slider
            (spread-props {:getAriaLabel (fn [] "time-indicator")
                           :min 0
                           :sx #js {:height "16px"
                                    :py 0
                                    :mb 1
                                    :pb 1
                                    :borderBottomColor "grey.60"
                                    :borderBottom 1
                                    :borderRadius 0
                                    "& .MuiSlider-thumb" #js {:transition "none"
                                                              :borderRadius "3px"
                                                              :transform "translateX(-1px)"
                                                              :height "16px"
                                                              :top 0
                                                              :width "2px"}
                                    "& .MuiSlider-valueLabel" #js {:transform "translateY(-18px)"
                                                                   :backgroundColor "primary.500"
                                                                   :borderRadius "3px"
                                                                   :px "0.25rem"
                                                                   ;; Thumb between label and the real thumb
                                                                   "&::before" #js {:width "2px"
                                                                                    :height "5px"
                                                                                    :bottom "-5px"
                                                                                    :transform "translateX(-1px)"}}
                                    "& .MuiSlider-valueLabelLabel" #js {:display "flex"}
                                    "& .MuiSlider-rail"  (spread-obj #js {:backgroundColor "grey.60"
                                                                          :borderRadius "3px"
                                                                          :transform "none"
                                                                          :top 0
                                                                          :height "16px"}
                                                                     (when timeline-selection
                                                                       (let [[start end] timeline-selection
                                                                             left (valueToPercent start min max)
                                                                             width (valueToPercent (- end start) min max)]
                                                                         (when start
                                                                           #js {:left (str left "%")
                                                                                :width (str width "%")}))))
                                    "& .selection-start, & .selection-end" #js {:content "\"\""
                                                                                :backgroundColor "grey.50"
                                                                                :borderRadius "3px"
                                                                                :opacity "0.36"
                                                                                :position "absolute"
                                                                                :height "16px"}
                                    "& .MuiSlider-track" #js {:backgroundColor "primary.500"
                                                              :border "none"
                                                              :borderRadius "3px"
                                                              :transform "none"
                                                              :transition "none"
                                                              :top 0
                                                              :height "16px"}
                                    "& .MuiSlider-mark" #js {:height "8px"
                                                             :backgroundColor "grey.60"
                                                             :top "unset"
                                                             :bottom 0
                                                             ;; Half width to left to position the mark based on center of the el
                                                             :transform "translate(-50%, 100%)"}
                                    "& .MuiSlider-markLabel" #js {:bottom 0}
                                    "& .MuiSlider-markLabel:nth-child(odd of .MuiSlider-markLabel)" #js {:display "none"}}
                           ;; Show mark for every other ts?
                           :marks (to-array (for [tick (generate-timeline-ticks max max-ticks)]
                                              #js {:value tick
                                                   :label (seconds-to-str tick)}))
                           :slots #js {;; FIXME: bad inline :slots fn
                                       :track (fn [^js props]
                                                ;; Modify position and width to use the start selection
                                                (if timeline-selection
                                                  (let [[start _end] timeline-selection
                                                        value (.-value (.-ownerState props))
                                                        left (valueToPercent start min max)
                                                        width (valueToPercent (- value start) min max)]
                                                    ($ SliderTrack
                                                       (spread-props props
                                                                     #js {:style (spread-obj (.-style props)
                                                                                             #js {:left (str left "%")
                                                                                                  :width (str width "%")})})))
                                                  ($ SliderTrack props)))
                                       :rail (fn [props]
                                               ;; Add extra elements for before & end rails
                                               (if timeline-selection
                                                 (let [[start end] timeline-selection
                                                       left (valueToPercent start min max)
                                                       right (valueToPercent end min max)]
                                                   ($ :<>
                                                      (when start
                                                        ($ :div.selection-start
                                                           {:style #js {:left "0%"
                                                                        :width (str "calc(" left "% - 8px)")}}))
                                                      ($ SliderRail props)
                                                      (when end
                                                        ($ :div.selection-end
                                                           {:style #js {:left (str "calc(" right "% + 8px)")
                                                                        :width (str (- 100 right) "%")}}))))
                                                 ($ SliderRail props)))}}
                          (dissoc props :timeline-selection)))))))
